import React, { useState } from 'react';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import GoogleOneTap from '../../components/GoogleOneTap';
import Logo from '../../images/logo.svg';
import './style.css';

const LoginPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login, loading, error, clearError } = useAuth();
    const { trackError, trackFormSubmitted } = useAnalytics();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile device
    React.useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
        setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    }, []);

    // Get return URL and action from location state
    const from = location.state?.from?.pathname || location.state?.from || '/dashboard';
    const action = location.state?.action;

    console.log('LoginPage - location.state:', location.state);
    console.log('LoginPage - from:', from);
    console.log('LoginPage - action:', action);

    // Validate identifier (email or phone)
    const validateEmail = (value) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) {
            return t('loginPage.emailRequired');
        }

        // If it contains @, treat as email
        if (value.includes('@')) {
            if (!emailRegex.test(value)) {
                return t('loginPage.invalidEmail');
            }
        } else {
            // Treat as phone number - basic length check
            const phoneRegex = /^\+?[\d\s-]{8,}$/;
            if (!phoneRegex.test(value)) {
                return t('loginPage.invalidPhone');
            }
        }
        return '';
    };

    // Validate password
    const validatePassword = (password) => {
        if (!password) {
            return t('loginPage.passwordRequired');
        }
        if (password.length < 8) {
            return t('loginPage.passwordMinLength');
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        clearError();

        // Validate inputs
        const emailErr = validateEmail(email);
        const passwordErr = validatePassword(password);

        setEmailError(emailErr);
        setPasswordError(passwordErr);

        if (emailErr || passwordErr) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await login(email, password);

            console.log('[LoginPage] Login response received:', response);

            // NOTE: Login tracking is handled by the backend to avoid double-counting
            // The backend tracks "login_completed" event with all relevant data

            // Track form submission success (for form UX analytics)
            console.log('[LoginPage] Tracking form submission success');
            trackFormSubmitted('login_form', true);

            toast.success(t('loginPage.loginSuccess'));

            // Determine redirect path based on action
            let redirectPath = from;

            // If defaulting to dashboard, check role to send to correct dashboard
            if (redirectPath === '/dashboard' || redirectPath === '/') {
                const userRole = response.user?.role;
                if (userRole === 'super_admin') {
                    redirectPath = '/admin';
                } else if (userRole === 'institution_admin') {
                    redirectPath = '/dashboard/institution';
                } else if (userRole === 'teacher') {
                    redirectPath = '/dashboard/teacher';
                } else {
                    redirectPath = '/dashboard';
                }
            }

            if (action === 'level-test') {
                redirectPath = '/level-test';
            } else if (action === 'demo') {
                redirectPath = '/demo-confirmation';
            } else if (action === 'subscribe') {
                redirectPath = '/subscribe';
            }

            console.log('LoginPage - redirecting to:', redirectPath);

            navigate(redirectPath, { replace: true });
        } catch (err) {
            const errorMessage = err.response?.data?.error?.message || t('loginPage.loginFailed');

            // Track form submission failure
            trackFormSubmitted('login_form', false, errorMessage);
            trackError('login_failed', errorMessage, {
                email: email,
                has_password: !!password,
            });

            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };



    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (emailError) {
            setEmailError('');
        }
        if (error) {
            clearError();
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (passwordError) {
            setPasswordError('');
        }
        if (error) {
            clearError();
        }
    };

    return (
        <div className="wpo-account-area">
            {/* Navigation Header */}
            <div className="auth-page-header">
                <Link to="/" className="back-to-home">
                    <FaArrowLeft />
                </Link>
                <Link to="/" className="auth-logo">
                    <img src={Logo} alt="Anas Logo" />
                </Link>
            </div>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-6">
                        <form onSubmit={handleSubmit} className="wpo-account-form">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <div className="wpo-account-form-area">
                                        <div className="wpo-logo">
                                            <h2>{t('loginPage.title')}</h2>
                                        </div>

                                        {/* Google One Tap / Sign In Button - Available on All Devices */}
                                        {true && (
                                            <div style={{ marginBottom: '20px' }}>
                                                <GoogleOneTap
                                                    autoPrompt={false}
                                                    showButton={true}
                                                    buttonText={t('loginPage.continueWithGoogle')}
                                                    context="signin"
                                                />
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    margin: '20px 0',
                                                    color: '#666'
                                                }}>
                                                    <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                                                    <span style={{ padding: '0 10px', fontSize: '14px' }}>{t('common.or')}</span>
                                                    <div style={{ flex: 1, height: '1px', background: '#ddd' }}></div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Error Alert */}
                                        {error && (
                                            <Alert severity="error" onClose={clearError} style={{ marginBottom: '20px' }}>
                                                {error}
                                            </Alert>
                                        )}

                                        <div className="wpo-account-form">
                                            <TextField
                                                fullWidth
                                                label={t('loginPage.emailOrWhatsapp')}
                                                variant="outlined"
                                                type="text"
                                                value={email}
                                                onChange={handleEmailChange}
                                                error={!!emailError}
                                                helperText={emailError}
                                                disabled={isSubmitting || loading}
                                                margin="normal"
                                            />
                                            <TextField
                                                fullWidth
                                                label={t('loginPage.password')}
                                                variant="outlined"
                                                type="password"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                error={!!passwordError}
                                                helperText={passwordError}
                                                disabled={isSubmitting || loading}
                                                margin="normal"
                                                // Fix autocomplete warning
                                                autoComplete="current-password"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={rememberMe}
                                                        onChange={(e) => setRememberMe(e.target.checked)}
                                                        color="primary"
                                                        disabled={isSubmitting || loading}
                                                    />
                                                }
                                                label={t('loginPage.rememberMe')}
                                            />
                                        </div>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            className="theme-btn"
                                            disabled={isSubmitting || loading}
                                        >
                                            {isSubmitting || loading ? (
                                                <>
                                                    <CircularProgress size={20} style={{ marginRight: '10px', color: 'white' }} />
                                                    {t('loginPage.loggingIn')}
                                                </>
                                            ) : (
                                                t('loginPage.loginButton')
                                            )}
                                        </Button>
                                        <div className="account-form-link">
                                            <p>{t('loginPage.noAccount')} <Link to="/signup">{t('signupNav')}</Link></p>
                                            <p><Link to="/forgot-password">{t('loginPage.forgotPasswordLink')}</Link></p>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;